import React from "react";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import noTableData from "../../assets/images/noTableData.svg";

const STAGES = [
  { key: "sourced",     label: "Sourced"     },
  { key: "screened",   label: "Screened"    },
  { key: "interviewed",label: "Interviewed" },
  { key: "offered",    label: "Offered"     },
  { key: "hired",      label: "Hired"       },
];

const BAR_COLOR   = "#6366F1";
const TRACK_COLOR = "#EEF2FF";
const LABEL_W     = 90;
const VALUE_W     = 150;
const BAR_H       = 28;
const ROW_GAP     = 20;

const PipelineFunnel = ({ data, loading }) => {
  const max     = data?.sourced || 1;
  const hasData = data && Object.values(data).some((v) => v > 0);

  const GRID_TICKS = 4;

  return (
    <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 2.5, height: "100%" }}>
      <Typography variant="h6" fontWeight={700} color="text.primary" mb={0.5}>
        Pipeline Conversion (Funnel)
      </Typography>

      {loading ? (
        <Box display="flex" height={220} justifyContent="center" alignItems="center">
          <CircularProgress sx={{ color: BAR_COLOR }} />
        </Box>
      ) : !hasData ? (
        <Box height={220} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
          <img src={noTableData} width={60} alt="No Data" />
          <Typography mt={0.75} fontWeight={500} fontSize={18} fontFamily="'Noto Sans', sans-serif">
            No pipeline data to show.
          </Typography>
        </Box>
      ) : (
        <Box mt={2}>
          {STAGES.map((stage, index) => {
            const count    = data?.[stage.key] ?? 0;
            const prevCount = index === 0 ? count : (data?.[STAGES[index - 1].key] ?? 0);
            const dropPct   = index === 0
              ? null
              : prevCount > 0 ? Math.round(((prevCount - count) / prevCount) * 100) : null;
            const barWidth  = max > 0 ? Math.max((count / max) * 100, 0) : 0;

            return (
              <Box key={stage.key} sx={{ display: "flex", alignItems: "center", mb: index < STAGES.length - 1 ? `${ROW_GAP}px` : 0 }}>
                {/* Stage label */}
                <Box sx={{ width: LABEL_W, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                    {stage.label}
                  </Typography>
                </Box>

                {/* Bar track */}
                <Box sx={{ flex: 1, bgcolor: TRACK_COLOR, borderRadius: "4px", height: BAR_H, overflow: "hidden", position: "relative" }}>
                  {/* Dashed vertical grid lines */}
                  {Array.from({ length: GRID_TICKS - 1 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        left: `${((i + 1) / GRID_TICKS) * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: "1px",
                        borderLeft: "1px dashed #C7D2FE",
                        zIndex: 1,
                      }}
                    />
                  ))}
                  {/* Filled bar */}
                  <Box
                    sx={{
                      width: `${barWidth}%`,
                      height: "100%",
                      bgcolor: BAR_COLOR,
                      borderRadius: "4px",
                      transition: "width 0.6s ease",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                </Box>

                {/* Count + drop */}
                <Box sx={{ width: VALUE_W, flexShrink: 0, textAlign: "right", pl: 1.5 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                    {count}
                  </Typography>
                  {dropPct !== null && (
                    <Typography component="span" variant="caption" sx={{ color: "#EF4444", ml: 0.75, fontWeight: 400 }}>
                      ({dropPct}% drop)
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default PipelineFunnel;
